import React, { createContext, useContext, useEffect, useState } from "react";
import { getAllCategoryNameData } from "services/HomeService";
import Cookies from "js-cookie";
import { setOnlineMenuCacheFromResponse } from "utils/onlineMenuCache";
import { unwrapMenuResponseBody } from "utils/onlineOrderMenuUtils";

interface OnlineOrderSettings {
    logo?: string;
    orderLeadTime?: number;
    image?: string;
    titleText?: string;
    customTextOne?: string;
    pauseOnlineOrder?: boolean;
    pauseOnlineOrderLabel?: string;
    onlineDeliveryId?: string;
    isDelivery?: boolean;
    phn?: string;
    /** Authorize.net Accept.js – from menu API; used to get payment nonce via Accept.dispatchData() */
    publicClientKey?: string;
    apiLogInId?: string;
    /** Basil Auth.Net – for delivery fees/tips; used for second Accept.js tokenization on delivery */
    basilPublicClientKey?: string;
    basilApiLogInId?: string;
    /** Kanopy Payment Token JS – gateway type and device GUIDs */
    onlinePaymentGateway?: string;   // "AUTHORIZE_NET" | "KANOPY"
    kanopyApiKey?: string;
    kanopyDeviceGuid?: string;
    basilKanopyApiKey?: string;
    basilKanopyDeviceGuid?: string;
}

interface ResponseData {
    onlineOrderSettings: OnlineOrderSettings;
    items: any[];
    publicClientKey?: string;
    apiLogInId?: string;
    basilPublicClientKey?: string;
    basilApiLogInId?: string;
    onlinePaymentGateway?: string;
    kanopyApiKey?: string;
    kanopyDeviceGuid?: string;
    basilKanopyApiKey?: string;
    basilKanopyDeviceGuid?: string;
}

interface ContextProps {
    settings: OnlineOrderSettings | null;
    items: any[];
    isLoading: boolean;
}

const OnlineOrderSettingsContext = createContext<ContextProps>({
    settings: null,
    items: [],
    isLoading: true,
});

export const useOnlineOrderSettings = () => useContext(OnlineOrderSettingsContext);

// Valid payment gateway values — the backend only sends these two.
// Validates sessionStorage to prevent XSS-injected values from being trusted.
const VALID_GATEWAYS = ["AUTHORIZE_NET", "KANOPY"] as const;

/** Type-safe read from sessionStorage with validation. Returns "" if missing or invalid. */
function readSessionValue(key: string): string {
    try {
        return sessionStorage.getItem(key)?.trim() ?? "";
    } catch {
        return ""; // sessionStorage unavailable (e.g. incognito in some browsers)
    }
}

// Kanopy gateway helpers — read values stored by OnlineOrderSettingsProvider from the menu API.
// The backend is the source of truth; these sessionStorage values are set from the /menu response.
export function isKanopyGateway(): boolean {
    const gw = readSessionValue("onlinePaymentGateway");
    return (VALID_GATEWAYS as readonly string[]).includes(gw) && gw === "KANOPY";
}

export function isAuthorizeNetGateway(): boolean {
    const gw = readSessionValue("onlinePaymentGateway");
    return (VALID_GATEWAYS as readonly string[]).includes(gw) && gw === "AUTHORIZE_NET";
}
export function getKanopyApiKey(): string {
    return readSessionValue("kanopyApiKey");
}
export function getKanopyDeviceGuid(): string {
    return readSessionValue("kanopyDeviceGuid");
}
export function getBasilKanopyApiKey(): string {
    return readSessionValue("basilKanopyApiKey");
}
export function getBasilKanopyDeviceGuid(): string {
    return readSessionValue("basilKanopyDeviceGuid");
}

export const OnlineOrderSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<OnlineOrderSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState<any[]>([]);

    const setFavicon = (imageUrl: any) => {
        const link = document.querySelector("link[rel='icon']");
        if (link && (link as HTMLLinkElement).href !== undefined) {
            (link as HTMLLinkElement).href = imageUrl;
        }
    };
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await getAllCategoryNameData();
                const resData = unwrapMenuResponseBody(response?.data) as
                  | ResponseData
                  | undefined;
                if (resData != null) {
                    setOnlineMenuCacheFromResponse(resData);
                }
                const data = resData?.onlineOrderSettings;
                const dataCategories = resData?.items ?? [];
                // Always publish menu categories so product detail can read them even if settings object is missing.
                setItems(dataCategories);
                if (data) {
                    if (data?.logo) sessionStorage.setItem("onlineOrderSettingsLogo", data.logo);
                    if (data?.titleText) Cookies.set("onlineOrderSettingsTitleText", data?.titleText);
                    if (data?.orderLeadTime) Cookies.set("onlineOrderSettingsLeadTime", data?.orderLeadTime.toString());
                    setSettings(data);
                    setFavicon(data?.logo);
                }
                // Accept.js: store credentials from menu API for tokenization (get payment nonce)
                const publicClientKey = data?.publicClientKey ?? resData?.publicClientKey;
                const apiLoginID = data?.apiLogInId ?? resData?.apiLogInId;
                if (publicClientKey) sessionStorage.setItem("acceptJsPublicClientKey", publicClientKey);
                if (apiLoginID) sessionStorage.setItem("acceptJsApiLoginID", apiLoginID);
                // Basil Accept.js (delivery fees/tips): from onlineOrderSettings
                const basilPublicClientKey = data?.basilPublicClientKey ?? resData?.basilPublicClientKey;
                if (basilPublicClientKey) sessionStorage.setItem("acceptJsBasilPublicClientKey", basilPublicClientKey);
                const basilApiLoginID = data?.basilApiLogInId ?? resData?.basilApiLogInId;
                if (basilApiLoginID) sessionStorage.setItem("acceptJsBasilApiLoginID", basilApiLoginID);
                // Kanopy Payment Token JS: gateway type and device GUIDs
                const gateway = data?.onlinePaymentGateway ?? resData?.onlinePaymentGateway;
                if (gateway) sessionStorage.setItem("onlinePaymentGateway", gateway);
                const kanopyApiKey = data?.kanopyApiKey ?? resData?.kanopyApiKey;
                if (kanopyApiKey) sessionStorage.setItem("kanopyApiKey", kanopyApiKey);
                const kanopyDeviceGuid = data?.kanopyDeviceGuid ?? resData?.kanopyDeviceGuid;
                if (kanopyDeviceGuid) sessionStorage.setItem("kanopyDeviceGuid", kanopyDeviceGuid);
                const basilKanopyApiKey = data?.basilKanopyApiKey ?? resData?.basilKanopyApiKey;
                if (basilKanopyApiKey) sessionStorage.setItem("basilKanopyApiKey", basilKanopyApiKey);
                const basilKanopyDeviceGuid = data?.basilKanopyDeviceGuid ?? resData?.basilKanopyDeviceGuid;
                if (basilKanopyDeviceGuid) sessionStorage.setItem("basilKanopyDeviceGuid", basilKanopyDeviceGuid);
            } catch (error) {
                console.error("Failed to fetch online order settings", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, []);

    return (
        <OnlineOrderSettingsContext.Provider value={{ settings, items, isLoading }}>
            {children}
        </OnlineOrderSettingsContext.Provider>
    );
};
